import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Truck, Banknote, CreditCard, Wallet, Building2, Loader2, ShieldCheck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/lib/hooks";
import { api, errMsg, img, tl } from "@/lib/api";

const METHODS = [
  { key: "eft", label: "Havale / EFT", desc: "IBAN'a ödeme, açıklamaya sipariş no", icon: Building2 },
  { key: "card", label: "Kredi / Banka Kartı", desc: "Online güvenli ödeme", icon: CreditCard },
  { key: "cod_cash", label: "Kapıda Nakit", desc: "Teslimatta nakit ödeme", icon: Banknote },
  { key: "cod_card", label: "Kapıda Kredi Kartı", desc: "Teslimatta kartla ödeme", icon: Wallet },
];

const Field = ({ label, id, ...rest }) => (
  <label className="block text-sm">
    <span className="font-semibold text-slate-700">{label}</span>
    <input data-testid={`checkout-${id}`} {...rest} className="mt-1 w-full h-11 border border-slate-300 rounded-md px-3 bg-white outline-none focus:border-[#172554] focus:ring-2 focus:ring-[#172554]/15" />
  </label>
);

export default function Checkout() {
  const { items, unit, clear } = useCart();
  const { user } = useAuth();
  const s = useSettings();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", district: "", address: "", note: "" });
  const [cargo, setCargo] = useState("");
  const [method, setMethod] = useState("eft");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "", phone: f.phone || user.phone || "" }));
  }, [user]);
  useEffect(() => {
    if (s && !cargo && s.cargo_companies?.length) setCargo(s.cargo_companies[0].name);
  }, [s, cargo]);

  const payload = { ...form, items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })), cargo, payment_method: method };
  const key = JSON.stringify([payload.items, cargo, method]);
  useEffect(() => {
    if (!items.length || !cargo) return;
    api.post("/orders/quote", { ...payload, name: "xx", email: "a@b.co", phone: "0000000000", city: "xx", address: "xxxxx" })
      .then((r) => setQuote(r.data)).catch((e) => toast.error(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/orders", payload);
      clear();
      if (data.card_live) {
        try {
          const { data: pay } = await api.post("/payments/start", { order_no: data.order_no });
          if (pay.type === "redirect") {
            window.location.assign(pay.url);
            return;
          }
          nav(`/siparis/${data.order_no}`, { state: { ...data, iframe: pay.url } });
        } catch (err) {
          toast.error(errMsg(err));
          nav(`/siparis/${data.order_no}`, { state: data });
        }
        return;
      }
      nav(`/siparis/${data.order_no}`, { state: data });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!items.length)
    return (
      <div className="max-w-xl mx-auto text-center py-24 px-4">
        <h1 className="text-3xl font-bold text-[#172554]">Sepetiniz boş</h1>
        <Link to="/" className="inline-block mt-6 px-6 py-3 rounded-md bg-[#172554] text-white font-bold" data-testid="checkout-continue-shopping">Alışverişe başla</Link>
      </div>
    );

  const methods = METHODS.filter((m) => s?.payments?.[m.key]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <form onSubmit={submit} className="max-w-7xl mx-auto px-4 lg:px-6 py-8 grid lg:grid-cols-[1fr_380px] gap-6">
      <div className="space-y-6">
        {!user && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-[#172554]" data-testid="checkout-member-hint">
            <b>Üye olun, kampanyaları kaçırmayın.</b> <Link to="/giris?tab=kayit&next=/sepet" className="underline font-semibold">Hemen üye ol</Link> veya üye olmadan devam edin.
          </div>
        )}
        <section className="bg-white border rounded-lg p-5 sm:p-6">
          <h2 className="text-xl font-bold text-[#172554] mb-4">Teslimat Bilgileri</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Ad Soyad" id="name" required value={form.name} onChange={set("name")} />
            <Field label="Telefon" id="phone" required type="tel" placeholder="05xx xxx xx xx" value={form.phone} onChange={set("phone")} />
            <Field label="E-posta" id="email" required type="email" value={form.email} onChange={set("email")} />
            <Field label="İl" id="city" required value={form.city} onChange={set("city")} />
            <Field label="İlçe" id="district" value={form.district} onChange={set("district")} />
            <Field label="Sipariş notu" id="note" value={form.note} onChange={set("note")} />
            <label className="sm:col-span-2 block text-sm">
              <span className="font-semibold text-slate-700">Açık adres</span>
              <textarea data-testid="checkout-address" required minLength={5} value={form.address} onChange={set("address")} rows={3} className="mt-1 w-full border border-slate-300 rounded-md p-3 outline-none focus:border-[#172554]" />
            </label>
          </div>
        </section>
        <section className="bg-white border rounded-lg p-5 sm:p-6">
          <h2 className="text-xl font-bold text-[#172554] mb-4">Kargo Firması</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {(s?.cargo_companies || []).map((c) => (
              <button type="button" key={c.name} data-testid={`checkout-cargo-${c.name}`} onClick={() => setCargo(c.name)}
                className={`text-left border rounded-md p-3 transition-colors ${cargo === c.name ? "border-[#172554] bg-blue-50 ring-1 ring-[#172554]" : "hover:border-slate-400"}`}>
                <Truck className="h-4 w-4 text-[#172554]" />
                <div className="text-sm font-bold mt-1">{c.name}</div>
                <div className="text-xs text-slate-500">{tl(c.price)}</div>
              </button>
            ))}
          </div>
          {s?.free_shipping_threshold > 0 && <p className="text-xs text-slate-500 mt-3">{tl(s.free_shipping_threshold)} ve üzeri siparişlerde kargo ücretsiz.</p>}
        </section>
        <section className="bg-white border rounded-lg p-5 sm:p-6">
          <h2 className="text-xl font-bold text-[#172554] mb-4">Ödeme Yöntemi</h2>
          <div className="grid sm:grid-cols-2 gap-2.5">
            {methods.map(({ key: k, label, desc, icon: Icon }) => (
              <button type="button" key={k} data-testid={`checkout-pay-${k}`} onClick={() => setMethod(k)}
                className={`flex gap-3 text-left border rounded-md p-4 transition-colors ${method === k ? "border-[#16A34A] bg-green-50 ring-1 ring-[#16A34A]" : "hover:border-slate-400"}`}>
                <Icon className="h-5 w-5 text-[#172554] mt-0.5" />
                <div><div className="font-bold text-sm">{label}</div><div className="text-xs text-slate-500">{desc}{k.startsWith("cod") && s?.cod_fee ? ` (+${tl(s.cod_fee)} hizmet bedeli)` : ""}</div></div>
              </button>
            ))}
          </div>
          {method === "eft" && (
            <div className="mt-4 space-y-2" data-testid="checkout-iban-list">
              {(s?.ibans || []).map((b) => (
                <div key={b.iban} className="border rounded-md p-3 bg-slate-50 text-sm"><b>{b.bank}</b> · {b.holder}<div className="font-mono mt-1">{b.iban}</div></div>
              ))}
              <p className="text-xs text-slate-500">Sipariş sonrası size verilecek sipariş numarasını açıklamaya yazmayı unutmayın.</p>
            </div>
          )}
          {method === "card" && s?.card_live && (
            <div className="mt-4 border rounded-md p-4 bg-green-50 border-green-200 text-sm text-green-900" data-testid="checkout-card-live-note">
              <ShieldCheck className="h-4 w-4 inline mr-1" /> Siparişi onayladıktan sonra {s.card_provider === "paytr" ? "PayTR" : "iyzico"} güvenli ödeme sayfasında kart bilgilerinizi gireceksiniz. Taksit seçenekleri orada gösterilir.
            </div>
          )}
          {method === "card" && !s?.card_live && (
            <div className="mt-4 border rounded-md p-4 bg-amber-50 border-amber-200 text-sm text-amber-900" data-testid="checkout-card-test-note">
              <ShieldCheck className="h-4 w-4 inline mr-1" /> Kart ödemesi şu an <b>TEST MODUNDA</b>. Gerçek tahsilat için {s?.card_provider === "paytr" ? "PayTR" : "iyzico"} hesap bilgileri bağlanacak; kart bilginiz istenmez ve kaydedilmez.
            </div>
          )}
        </section>
      </div>
      <aside className="lg:sticky lg:top-28 h-fit bg-white border rounded-lg p-5 sm:p-6 space-y-4" data-testid="checkout-summary">
        <h2 className="text-xl font-bold text-[#172554]">Sipariş Özeti</h2>
        {items.map((i) => (
          <div key={i.product_id} className="flex gap-3 text-sm">
            <img src={img(i.image)} alt="" className="h-12 w-12 object-contain border rounded" />
            <div className="flex-1"><div className="font-semibold line-clamp-1">{i.name}</div><div className="text-slate-500">{i.qty} x {tl(unit(i))}</div></div>
          </div>
        ))}
        <div className="border-t pt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span>Ara toplam</span><span data-testid="checkout-subtotal">{tl(quote?.subtotal)}</span></div>
          <div className="flex justify-between"><span>Kargo</span><span data-testid="checkout-shipping">{quote?.shipping ? tl(quote.shipping) : "Ücretsiz"}</span></div>
          {quote?.cod_fee > 0 && <div className="flex justify-between"><span>Kapıda ödeme bedeli</span><span>{tl(quote.cod_fee)}</span></div>}
          <div className="flex justify-between text-lg font-extrabold text-[#172554] pt-2"><span>Toplam</span><span data-testid="checkout-total">{tl(quote?.total)}</span></div>
        </div>
        <button data-testid="checkout-submit-button" disabled={busy} className="w-full h-12 rounded-md bg-[#16A34A] hover:bg-[#15803D] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Siparişi Onayla
        </button>
      </aside>
    </form>
  );
}
